import type { FC } from "react";

/**
 * Wordmark CUB como SVG inline — o pacote npm não embarca assets de imagem
 * (mesmo motivo do `CubMark` da Sidebar). Arte exportada do Figma com o `fill`
 * trocado por `currentColor`, então a cor vem do contexto: `blue` no
 * `AppHeader`, `white`/`cobalt` ou a cor do parceiro no `AppFooter`.
 *
 * Vive fora das pastas de componente porque `AppHeader` e `AppFooter` usam a
 * mesma arte — mesmo movimento do `cubScrollbarSx`, que saiu do `Modal` em
 * 0.5.0 quando ganhou um segundo consumidor.
 */

/**
 * `overflow: visible` não é decoração. A arte encosta **exatamente** nas duas
 * bordas horizontais do `viewBox` (a caixa dos paths vai de y=0 a y=32, sem
 * folga), e `<svg>` recorta no próprio box por padrão. Como a altura é
 * fracionária — 102×32 não dá altura inteira em largura nenhuma da spec
 * (60 → 18,82px · 80 → 25,10px · 90 → 28,24px) — o arredondamento na
 * rasterização raspa a linha de baixo do glifo, e nas larguras pequenas isso
 * vira um corte reto visível na base das letras.
 *
 * Alternativas descartadas: arredondar a altura para cima muda a caixa medida
 * contra a spec; mexer no `viewBox` para criar folga desalinha a arte de
 * origem no Figma.
 */
const base = { display: "block", flexShrink: 0, overflow: "visible" } as const;

/** Wordmark CUB. Proporção 102×32 — passe a largura, a altura acompanha. */
export const CubWordmark: FC<{ width?: number }> = ({ width = 90 }) => (
  <svg
    width={width}
    height={(width * 32) / 102}
    viewBox="0 0 102 32"
    fill="none"
    role="img"
    aria-label="CUB"
    style={base}
  >
    <path
      d="M31.8948 20.9437V19.6581C31.8948 19.2914 31.5984 18.9939 31.2329 18.9939H22.1985C21.8328 18.9939 21.5365 19.2914 21.5365 19.6581V20.9432C21.5365 21.3099 21.2401 21.6073 20.8745 21.6073H11.0205C10.6548 21.6073 10.3585 21.3099 10.3585 20.9432V11.0568C10.3585 10.69 10.6548 10.3926 11.0205 10.3926H20.8745C21.2401 10.3926 21.5365 10.69 21.5365 11.0568V12.3418C21.5365 12.7086 21.8328 13.006 22.1985 13.006H31.2329C31.5984 13.006 31.8948 12.7086 31.8948 12.3418V11.0563C31.8948 4.95009 26.961 0 20.8748 0H11.0199C4.93384 0 0 4.95009 0 11.0563V20.9437C0 27.0499 4.93384 31.9999 11.0199 31.9999H20.8749C26.961 31.9999 31.8948 27.0499 31.8948 20.9437Z"
      fill="currentColor"
    />
    <path
      d="M45.2977 0.664342V20.9431C45.2977 21.31 45.5941 21.6074 45.9597 21.6074H55.8136C56.1792 21.6074 56.4756 21.31 56.4756 20.9431V0.664342C56.4756 0.29757 56.772 0.000183105 57.1376 0.000183105H66.1721C66.5376 0.000183105 66.8341 0.29757 66.8341 0.664342V20.9435C66.8341 27.0498 61.9002 31.9998 55.8141 31.9998H45.9591C39.8731 31.9998 34.9392 27.0498 34.9392 20.9435V0.664342C34.9392 0.29757 35.2356 0.000183105 35.6012 0.000183105H44.6357C45.0013 0.000183105 45.2977 0.29757 45.2977 0.664342Z"
      fill="currentColor"
    />
    <path
      d="M91.2298 24.3852H80.4368C80.0712 24.3852 79.7749 24.0878 79.7749 23.721V19.7916C79.7749 19.4248 80.0712 19.1274 80.4368 19.1274H91.2298C91.596 19.1274 91.892 19.4248 91.892 19.7916V23.721C91.892 24.0878 91.596 24.3852 91.2298 24.3852ZM80.4368 7.70538H90.9138C91.2791 7.70538 91.5759 8.00277 91.5759 8.36954V12.299C91.5759 12.6658 91.2791 12.9632 90.9138 12.9632H80.4368C80.0712 12.9632 79.7749 12.6658 79.7749 12.299V8.36954C79.7749 8.00277 80.0712 7.70538 80.4368 7.70538ZM99.1089 15.9547C100.78 14.7309 101.774 12.6458 101.774 9.5184V9.06509C101.684 3.21816 98.6566 0 91.0225 0H70.5547C70.1892 0 69.8928 0.297387 69.8928 0.664159V31.3357C69.8928 31.7026 70.1892 31.9999 70.5547 31.9999H91.3385C98.9734 31.9999 102 28.9178 102 23.1161V22.7081C102 19.3994 100.87 17.2237 99.1089 15.9547Z"
      fill="currentColor"
    />
  </svg>
);
